import { useCallback, useContext, useMemo, useState } from "react";
import { AvisConstants } from "../../../../../../../data/avisConstants";
import {
  ItemViewModel,
  LicenseCreationInfo,
  LicenseCreationsFilter,
} from "../../../../../../../generated/data-contracts";
import { IAxiosError, TableUtils, api } from "../../../../../../../utils";
import { AvisForm } from "../../../../../../shared/components";
import {
  AvisDataRenewDate,
  AvisDescriptiveText,
} from "../../../../../../shared/components/form";
import {
  AvisFilterableTable,
  AvisSimpleTable,
  IColumnConfig,
} from "../../../../../../shared/components/table";
import { AvisContext } from "../../../../../../shared/context/AvisContext";
import { LicenseCreationsFilterForm } from "./filter/LicenseCreationsFilterForm";
import { validationCreationPicker } from "./validation";

interface ICreationsPickerProps {
  isViewSelectedView: boolean;
  selectedReadOnly: number[];
  selectedPublicCreations: LicenseCreationInfo[];
  setSelectedPublicCreations: React.Dispatch<
    React.SetStateAction<LicenseCreationInfo[]>
  >;
  setIsViewSelectedView: React.Dispatch<React.SetStateAction<boolean>>;
  creationClassesPublic: ItemViewModel[];
}
export function CreationsPicker(props: Readonly<ICreationsPickerProps>) {
  const {
    isViewSelectedView,
    selectedPublicCreations,
    selectedReadOnly,
    creationClassesPublic,
    setSelectedPublicCreations,
    setIsViewSelectedView,
  } = props;
  const { res } = useContext(AvisContext);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const defaultFilterValues: LicenseCreationsFilter = {
    creationClassId: undefined,
    creationNameFilterType: "Contains",
    creationName: "",
    searchAllNames: true,
    pageIndex: 0,
    pageSize: 10,
  };

  const [error, setError] = useState<IAxiosError>();

  const onCreationSelectionChange = useCallback(
    (rowData: LicenseCreationInfo, selected: boolean) => {
      if (selected) {
        setSelectedItems((prev) => {
          const itemAlreadyAdded =
            prev.filter((p) => p === rowData.creationId).length > 0;
          const result =
            itemAlreadyAdded || !rowData.creationId
              ? prev
              : [...prev, rowData.creationId];

          return result;
        });

        setSelectedPublicCreations((prev) => {
          const itemAlreadyAdded =
            prev.filter((p) => p.creationId === rowData.creationId).length > 0;
          const result = itemAlreadyAdded ? prev : [...prev, rowData];

          return result;
        });
        return;
      }

      setSelectedItems((prev) => {
        const result = prev.filter(
          (item) => rowData.creationId && item !== rowData.creationId,
        );

        //If last item is unselected, then exit preview mode
        if (result.length === 0 && isViewSelectedView) {
          setIsViewSelectedView(false);
        }

        return result;
      });
      setSelectedPublicCreations((prev) => {
        const result = prev.filter(
          (item) => item?.creationId !== rowData.creationId,
        );

        return result;
      });
    },
    [isViewSelectedView, setIsViewSelectedView, setSelectedPublicCreations],
  );

  const columnMetadata: IColumnConfig[] = useMemo(() => {
    return [
      TableUtils.columnConfigCheckbox<LicenseCreationInfo>(
        "creationId",
        " ",
        selectedReadOnly,
        selectedItems,
        onCreationSelectionChange,
      ),
      TableUtils.columnConfigString<LicenseCreationInfo>(
        "creationNameToDisplay",
        res("label_Title"),
      ),
      TableUtils.columnConfigString<LicenseCreationInfo>(
        "creationClassName",
        res("label_CreationClassName"),
      ),
      TableUtils.columnConfigStringHTMLEnabled<LicenseCreationInfo>(
        "rightsHolderNamesToDisplay",
        res("label_Author"),
      ),
      TableUtils.columnConfigString<LicenseCreationInfo>(
        "bandsToDisplay",
        res("label_Performer"),
      ),
    ];
  }, [onCreationSelectionChange, res, selectedItems, selectedReadOnly]);

  const descriptiveTextAndRenewDate = useMemo(
    () => (
      <>
        <AvisDescriptiveText textKey="portal_LIC_ProgramReportCreationSearch" />
        <AvisDataRenewDate dataType="licencePublicCreationsUpdateDate" />
      </>
    ),
    [],
  );

  const tableColumnWidths = useMemo(() => [5, 30, 15, 15, 15], []);

  const renderContent = () => {
    if (isViewSelectedView) {
      return (
        <>
          {descriptiveTextAndRenewDate}
          <h1>
            {res(
              "licensees_LicenseReports_PP_section_Identified_Modal_PreviewTitle",
            )}
          </h1>
          <AvisSimpleTable
            columnMetadata={columnMetadata}
            data={selectedPublicCreations}
            columnWidths={tableColumnWidths}
          />
        </>
      );
    }

    return (
      <>
        {descriptiveTextAndRenewDate}
        <AvisFilterableTable<LicenseCreationInfo, LicenseCreationsFilter>
          defaultFilterValues={defaultFilterValues}
          filterSessionKey={AvisConstants.licenses_creations_filter_values_key}
          filterValidationSchema={validationCreationPicker(res)}
          setError={setError}
          loadDataOnInit={false}
          getApiData={(filter) =>
            api.licenseesGetLicenseCreations(filter as any)
          }
          renderFilter={(bag, loading, filtered) => (
            <LicenseCreationsFilterForm
              bag={bag}
              loading={loading}
              creationClasses={creationClassesPublic}
              filtered={filtered}
            />
          )}
          tableColumns={columnMetadata}
          forceUpdateKey={selectedItems.length}
          tableColumnWidths={tableColumnWidths}
        />
      </>
    );
  };

  return (
    <AvisForm className="search-public-creations-form" error={error}>
      {renderContent()}
    </AvisForm>
  );
}
